import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Save, PlusCircle, List, BarChart2, Info, Gem, FileText, ChevronDown } from 'lucide-react';
import WorkshopExportMenu from '../../common/WorkshopExportMenu';

const BusinessValuesHeader = ({
  viewMode,
  setViewMode,
  handleSaveData,
  onAddClick,
  handleRefreshData,
  handlePrint,
  handleExportPDF,
  handleExportPDFEn,
  handleExportDocx,
  handleExportDocxEn,
  isAddFormVisible,
  businessValues,
  onOpenGuide,
  onImport
}) => {
  const { t } = useTranslation();
  const [isPdfMenuOpen, setIsPdfMenuOpen] = useState(false);
  const [isDocxMenuOpen, setIsDocxMenuOpen] = useState(false);
  const [isImportMenuOpen, setImportMenuOpen] = useState(false);

  // Calculate statistics
  const businessValuesStats = useMemo(() => {
    const totalValues = businessValues?.length || 0;
    const totalAssets = businessValues?.reduce((sum, bv) => sum + (bv.supportAssets?.length || 0), 0) || 0;
    const securityPillarsCount = new Set(
      businessValues?.flatMap(bv => bv.securityPillars || [])
    ).size;

    return {
      values: totalValues,
      assets: totalAssets,
      pillars: securityPillarsCount
    };
  }, [businessValues]);

  return (
    <>
      {/* === MERGED HEADER === */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-4 mb-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          {/* Left Side: Breadcrumb & Title */}
          <div>
            <div className="flex items-center text-sm text-slate-500 mb-2">
              <span className="hover:text-blue-600 transition-colors cursor-pointer">{t('navigation.workshop1') || 'Atelier 1'}</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mx-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-blue-600 font-medium">{t('businessValues.title')}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center">
              <Gem size={28} className="mr-3 text-blue-600" />
              {t('businessValues.title')}
            </h1>
          </div>

          {/* Right Side: Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Unified Export Menu */}
            <WorkshopExportMenu
              onExportPdfFr={handleExportPDF}
              onExportPdfEn={handleExportPDFEn}
              onExportDocxFr={handleExportDocx}
              onExportDocxEn={handleExportDocxEn}
            />

            {/* Import Dropdown */}
            <div className="relative">
              <button
                onClick={() => setImportMenuOpen(!isImportMenuOpen)}
                className="text-sm font-medium bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center shadow-sm transition duration-200"
                title={t('businessValues.fields.importTemplate') || "Importer / Modèles"}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                {t('businessValues.fields.importTemplate') || "Import / Modèles"}
                <ChevronDown size={14} className={`ml-2 transition-transform duration-200 ${isImportMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {isImportMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-slate-100 overflow-hidden z-20"
                  >
                    {/* Import Action */}
                    <div className="border-b border-slate-100">
                      <input
                        type="file"
                        accept=".csv"
                        className="hidden"
                        id="csv-upload"
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            onImport(e.target.files[0]);
                            e.target.value = '';
                            setImportMenuOpen(false);
                          }
                        }}
                      />
                      <label
                        htmlFor="csv-upload"
                        className="w-full text-left px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center cursor-pointer font-medium"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        {t('businessValues.fields.importCSV') || 'Importer CSV'}
                      </label>
                    </div>

                    {/* Templates */}
                    <div className="py-1 bg-slate-50">
                      <p className="px-4 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('businessValues.fields.templates') || 'Modèles'}</p>
                      <a
                        href="/templates/import_business_values_fr.csv"
                        download="import_business_values_fr.csv"
                        className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 transition-colors flex items-center"
                        onClick={() => setImportMenuOpen(false)}
                      >
                        <span className="fi fi-fr mr-2"></span>
                        {t('businessValues.fields.downloadTemplateFR') || 'Modèle CSV (FR)'}
                      </a>
                      <a
                        href="/templates/import_business_values_en.csv"
                        download="import_business_values_en.csv"
                        className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 transition-colors flex items-center"
                        onClick={() => setImportMenuOpen(false)}
                      >
                        <span className="fi fi-gb mr-2"></span>
                        {t('businessValues.fields.downloadTemplateEN') || 'Template CSV (EN)'}
                      </a>
                    </div>
                  </motion.div>
                )}

              </AnimatePresence>
            </div>

            {/* Save Button */}
            <button
              onClick={handleSaveData}
              className="text-sm font-medium bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center shadow-sm transition duration-200"
            >
              <Save size={16} className="mr-2" />
              {t('businessValues.buttons.save')}
            </button>
          </div>
        </div>

        {/* Info Boxes - Compact & Less Vertical Space */}
        <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-slate-100 print:hidden">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center text-slate-600 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
              <Gem size={16} className="text-blue-600 mr-2" />
              <span className="font-semibold text-slate-800 mr-1">{businessValuesStats.values}</span>
              <span className="text-slate-500">{t('businessValues.stats.values', 'Valeurs Métier')}</span>
            </div>

            <div className="w-px h-4 bg-slate-200"></div>

            <div className="flex items-center text-slate-600 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
              <Info size={16} className="text-amber-600 mr-2" />
              <span className="font-semibold text-slate-800 mr-1">{businessValuesStats.assets}</span>
              <span className="text-slate-500">{t('businessValues.stats.assets', 'Biens Supports')}</span>
            </div>

            <div className="w-px h-4 bg-slate-200"></div>

            <div className="flex items-center text-slate-600 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
              <Info size={16} className="text-green-600 mr-2" />
              <span className="font-semibold text-slate-800 mr-1">{businessValuesStats.pillars}</span>
              <span className="text-slate-500">{t('businessValues.stats.pillars', 'Piliers Sécurité')}</span>
            </div>
          </div>

          {/* View Mode Toggle - Integrated cleanly */}
          <div className="flex items-center bg-slate-100/80 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('list')}
              className={`relative px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${viewMode === 'list'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
                }`}
            >
              <List size={14} />
              {t('businessValues.buttons.list')}
            </button>
            <button
              onClick={() => setViewMode('chart')}
              className={`relative px-3 py-1.5 rounded-md text-xs font-semibold transition-all flex items-center gap-1.5 ${viewMode === 'chart'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
                }`}
            >
              <BarChart2 size={14} />
              {t('businessValues.buttons.visualize')}
            </button>
          </div>
        </div>


      </div>
      {/* === END MERGED HEADER === */}
    </>
  );
};

export default BusinessValuesHeader;
